import { Link } from 'react-router-dom';
import CTASection from '../../components/CTASection/CTASection';
import CertificationBadges from '../../components/CertificationBadges/CertificationBadges';
import EngagementProcess from '../../components/EngagementProcess/EngagementProcess';
import PageHero from '../../components/PageHero/PageHero';
// The hero photograph this page has always used. servicesb.jpg itself is
// 5138x3239 and 15.4 MB -- more than a hero at 1140px ever needs, and the
// single worst asset on the site (audit P0-1). This is the same image resized
// to 2000px wide at 360 KB. The original is untouched in src/assets.
import heroImage from '../../assets/servicesb-hero.jpg';
import Seo, { seoPropsFor } from '../../components/Seo/Seo';
import { CERTIFICATIONS, SHOW_CERTIFICATIONS } from '../../content/certifications';
import { PRACTICES, SERVICES_PAGE_COPY, practicePath } from '../../content/services';
import { PAGE_SEO } from '../../content/site';
import styles from './Services.module.scss';

/**
 * /services -- the overview.
 *
 * Its job is routing, not selling. Five sections: a line covering both
 * practices, a card per practice, the certifications, how an engagement runs,
 * and a CTA. Anyone landing here wants to reach the right hub in one click,
 * so nothing else goes on this page -- the depth lives on the hubs and the
 * service pages.
 *
 * What used to be here: an "Our Services" intro block and an "Our Service
 * Approach" section of three photographic cards. The cards were a fourth
 * description of what the company does, alongside eleven services and two
 * practices, and one of them carried the unverified "ServiceNow certified
 * instructors and curriculum developers" claim. Their three background images
 * plus the hero photograph were the bulk of the ~61 MB this page loaded.
 */
const ServicesPage = () => (
  <div className={styles.servicesPage}>
    <Seo {...seoPropsFor(PAGE_SEO.services)} />

    <div className="main-content">
      <PageHero
        title={SERVICES_PAGE_COPY.hero.title}
        subtitle={SERVICES_PAGE_COPY.hero.lead}
        image={heroImage}
        tone="services"
      />

      <section className={styles.practiceCards}>
        <div className={styles.container}>
          <div className={styles.practiceGrid}>
            {PRACTICES.map((practice) => (
              <article key={practice.slug} className={styles.practiceCard}>
                <h2>
                  <Link to={practicePath(practice)}>{practice.name}</Link>
                </h2>
                <p className={styles.practicePositioning}>{practice.positioning}</p>
                <p className={styles.serviceCount}>
                  {practice.services.length} services
                </p>
                <Link to={practicePath(practice)} className={styles.practiceLink}>
                  {`View ${practice.name} Solutions`}
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* The certification strip that sat here is switched off with the rest
          of the certification display -- see SHOW_CERTIFICATIONS in
          content/certifications.ts. Nothing takes its place: the page is four
          sections rather than five with a gap in it. */}
      {SHOW_CERTIFICATIONS && (
        <section className={styles.certificationStrip}>
          <div className={styles.container}>
            <CertificationBadges
              certifications={CERTIFICATIONS}
              label={SERVICES_PAGE_COPY.certificationsLabel}
            />
          </div>
        </section>
      )}

      {/* Understand / Design / Build / Enable / Improve. The same section the homepage
          renders, from the same content -- see components/EngagementProcess. */}
      <EngagementProcess />
    </div>

    <CTASection
      heading="Not sure where to start?"
      body="Tell us about your environment and your current priorities. We will recommend the right starting point."
      ctaLabel="Contact Us"
      ctaHref="/contact"
    />
  </div>
);

export default ServicesPage;
